"use client"

import { createContext, useContext } from "react"

const ApartContext = createContext()

export const useApartContext = () => {
  const context = useContext(ApartContext)
  if (!context) {
    throw new Error("useApartContext must be used within an ApartContextProvider")
  }
  return context
}

const ApartContextProvider = ({ children }) => {
  const apartmentData = {
    id: 2,
    titleEn: "✨Jerusalem View✨",
    titleEs: "✨Jerusalem View✨",
    titleFr: "✨Jerusalem View✨",
    titleHe: "✨נופי ירושלים✨",
    internalCode: "(H51)",
    descriptionEn: "Spacious 3-Bedroom Apartment with Stunning Jerusalem View – Steps from Mahane Yehuda Market.",
    descriptionEs: "Amplio apartamento de 3 dormitorios con impresionantes vistas a Jerusalén, a pasos del mercado Mahane Yehuda.",
    descriptionFr: "Spacieux appartement de 3 chambres avec vue imprenable sur Jérusalem – à quelques pas du marché Mahane Yehuda.",
    descriptionHe: "דירת 3 חדרי שינה מרווחת עם נוף עוצר נשימה לירושלים – צעדים משוק מחנה יהודה.",
    longDescriptionEn: "Welcome to your home away from home in the heart of Jerusalem!\n\nThis beautifully designed 3-bedroom apartment with a large living room offers the perfect combination of comfort, convenience, and breathtaking views. Located just a minute's walk from the vibrant Mahane Yehuda Market, and right next to the light rail station, supermarkets, cafés, and public transportation – everything you need is just outside your door.\n\nEnjoy panoramic views of Jerusalem from the apartment's windows and balcony – especially stunning at sunset, when the city lights begin to glow.",
    longDescriptionEs: "¡Bienvenido a su hogar lejos del hogar en el corazón de Jerusalén! [...]",
    longDescriptionFr: "Bienvenue dans votre maison loin de chez vous au cœur de Jérusalem! [...]",
    longDescriptionHe: `ברוכים הבאים לביתכם הרחק מהבית, בלב ירושלים!

הדירה המעוצבת כוללת 3 חדרי שינה וסלון גדול, ומשלבת נוחות, נגישות ונוף עוצר נשימה. ממוקמת במרחק דקה הליכה משוק מחנה יהודה השוקק, ובסמוך לתחנת הרכבת הקלה, סופרמרקטים, בתי קפה ותחבורה ציבורית – כל מה שצריך נמצא ממש מחוץ לדלת.

תיהנו מנוף פנורמי של ירושלים מהחלונות ומהמרפסת – במיוחד בזמן השקיעה, כשהאורות של העיר מתחילים לזהור.`,
    size: 110,
    guests: 6,
    bedrooms: 3,
    bathrooms: 2,
    images: [
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569659/Apart_2_2_koqn9x.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569656/Apart_2_1_xlmtqq.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569657/Apart_2_3_iddzzp.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569657/Apart_2_4_mwhwaz.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569659/Apart_2_5_govb4g.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569658/Apart_2_6_ti1rhl.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569660/Apart_2_7_ctoek3.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569659/Apart_2_8_fb9mia.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569661/Apart_2_9_yewbij.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569662/Apart_2_10_m2aid2.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569662/Apart_2_11_u7qc4i.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569663/Apart_2_12_enypjb.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569664/Apart_2_13_bawojr.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569665/Apart_2_14_mjkxjq.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569666/Apart_2_15_rbow4x.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569666/Apart_2_16_nnzmtx.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569668/Apart_2_17_hglq8n.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569668/Apart_2_18_gkmhjw.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569670/Apart_2_19_bilyek.png"
    ],
    amenities: [
      { name: "Fully Equipped Kitchen", nameEs: "Cocina Totalmente Equipada", nameFr: "Cuisine Entièrement Équipée", nameHe: "מטבח מאובזר", icon: "kitchen" },
      { name: "Air Conditioning", nameEs: "Aire Acondicionado", nameFr: "Climatisation", nameHe: "מיזוג אוויר", icon: "air" },
      { name: "Smart TV", nameEs: "Smart TV", nameFr: "Smart TV", nameHe: "טלוויזיה חכמה", icon: "tv" },
      { name: "High-Speed Wi-Fi", nameEs: "Wi-Fi de Alta Velocidad", nameFr: "Wi-Fi Haut Débit", nameHe: "אינטרנט אלחוטי מהיר", icon: "wifi" },
      { name: "Washing Machine", nameEs: "Lavadora", nameFr: "Machine à Laver", nameHe: "מכונת כביסה", icon: "washer" },
      { name: "Nespresso Machine", nameEs: "Máquina Nespresso", nameFr: "Machine Nespresso", nameHe: "מכונת נספרסו", icon: "coffee" },
      { name: "Fresh Linens & Towels", nameEs: "Sábanas y Toallas Limpias", nameFr: "Draps et Serviettes Frais", nameHe: "מצעים ומגבות נקיים", icon: "bed" },
      { name: "Hairdryer", nameEs: "Secador de Pelo", nameFr: "Sèche-cheveux", nameHe: "מייבש שיער", icon: "hairdryer" },
      { name: "Iron", nameEs: "Plancha", nameFr: "Fer à Repasser", nameHe: "מגהץ", icon: "iron" },
      { name: "Dryer", nameEs: "Secadora", nameFr: "Sèche-linge", nameHe: "מייבש כביסה", icon: "dryer" }
    ],
    location: {
      addressEn: "103 Jaffa Street, Jerusalem",
      addressEs: "103 Calle Jaffa, Jerusalén",
      addressFr: "103 Rue Jaffa, Jérusalem",
      addressHe: "רחוב יפו 103, ירושלים",
      addressDescEn: "Near Mahane Yehuda Market, Jerusalem",
      addressDescEs: "Cerca del Mercado Mahane Yehuda, Jerusalén",
      addressDescFr: "Près du marché Mahane Yehuda, Jérusalem",
      addressDescHe: "בקרבת שוק מחנה יהודה, ירושלים",
      coordinates: {
        lat: 31.7857,
        lng: 35.2124
      },
      highlights: [
        { nameEn: "1-minute walk to Mahane Yehuda Market", nameEs: "...", nameFr: "...", nameHe: "דקה הליכה משוק מחנה יהודה" },
        { nameEn: "Light rail station directly across the street", nameEs: "...", nameFr: "...", nameHe: "תחנת הרכבת הקלה ממש מול הדירה" },
        { nameEn: "Surrounded by shops, supermarkets, restaurants, and cafés", nameEs: "...", nameFr: "...", nameHe: "מוקפת חנויות, סופרמרקטים, מסעדות ובתי קפה" },
        { nameEn: "Easy access to all major attractions in Jerusalem", nameEs: "...", nameFr: "...", nameHe: "גישה נוחה לכל האטרקציות המרכזיות בירושלים" }
      ]
    }
  };

  return (
    <ApartContext.Provider value={{ apartmentData }}>
      {children}
    </ApartContext.Provider>
  )
}

export default ApartContextProvider
